import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { styled, Theme, useMediaQuery } from "@mui/material";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_TableOptions,
} from "material-react-table";
import * as React from "react";
import { JSX, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Guid from "_shared/guids/guid";
import { CanceledError } from "axios";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import AdministratorInvitationAPIService from "legal-entities/invitations/administrator-invitations/_api/administrator-invitation-api-service";
import AdministratorInvitation from "legal-entities/invitations/administrator-invitations/administrator-invitation";
import LegalEntity from "legal-entities/legal-entity";
import { enqueueSnackbar } from "notistack";
import { useSession } from "users/__session/session-context";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSelector } from "react-redux";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EntityName from "legal-entities/entity-names/entity-name";
import { MRTNoMemo } from "_shared/_views/tables/table";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

type AdministratorInvitationsTableProps = {
  renderTopToolbarCustomActions: (props: {
    table: TableInstance<AdministratorInvitation>;
    additional?: JSX.Element;
  }) => ReactNode;
};

const membersColumnHelper = createMRTColumnHelper<AdministratorInvitation>();

export default function AdministratorInvitationsTable(props: Readonly<AdministratorInvitationsTableProps>) {
  const { renderTopToolbarCustomActions } = props;

  // State
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [data, setData] = React.useState<AdministratorInvitation[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Hooks
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const fetchData = React.useCallback(async () => {
    // If permissions are still loading/unknown, don't fetch yet
    if (sessionPermissions === undefined) return;

    setWasErrorLoading(false);
    setIsLoading((prev) => (data.length === 0 ? true : prev));
    setIsRefetching((prev) => (data.length > 0 ? true : prev));

    try {
      if (!sessionPermissions.includesAccessToAdministration) {
        setData([]);
        setRowCount(0);
        setWasErrorLoading(true);
        return;
      }

      const legalEntity = LegalEntity.create({
        id: session.user!.legalEntityId,
        name: new EntityName(session.user!.companyName!),
      });

      const adminService = new AdministratorInvitationAPIService(session);
      const invitations = await adminService.getAdminInvitationsForLegalEntity(legalEntity);

      setData(invitations ?? []);
      setRowCount(invitations?.length ?? 0);
      setWasErrorLoading(false);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setData([]);
      setRowCount(0);
      setWasErrorLoading(true);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  }, [session, sessionPermissions, data.length]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);


  const renderNameColumn = React.useCallback((data: AdministratorInvitation): React.ReactNode => {
    const invitation: AdministratorInvitation = data;
    const name = `${invitation.registrationInfo?.name?.firstName} ${invitation.registrationInfo?.name?.lastName}`;
    const email = invitation.registrationInfo?.email?.value;
    const contactLink = <RowTitle>{name}</RowTitle>;

    return (
      <NameCellContainer>
        <NameText primary={contactLink} secondary={email} />
      </NameCellContainer>
    );
  }, []);

  const renderStatusColumn = React.useCallback((data: AdministratorInvitation): React.ReactNode => {
    return data?.status?.valueOf() ?? "Unknown";
  }, []);

  const handleCancelInvite = React.useCallback(async (invitation?: AdministratorInvitation) => {
    if (!invitation) return;

    const adminName = `${invitation.registrationInfo.name.firstName} ${invitation.registrationInfo.name.lastName}`;

    const response = await confirm({
      title: "Cancel Administrator Invitation?",
      message: `Cancel ${adminName}'s administrator invitation to your organization?`,
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const service = new AdministratorInvitationAPIService(session);
      await service.cancelAdministratorInvitation(invitation);
      enqueueSnackbar("Canceled invitation", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }, [confirm, session]);

  const columns = React.useMemo(() => [
    membersColumnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    membersColumnHelper.accessor(renderStatusColumn, {
      header: "Status",
      id: "status",
    }),
  ], [renderNameColumn, renderStatusColumn]);

  const tableOptions: MRT_TableOptions<AdministratorInvitation> = {
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button startIcon={<PersonAddIcon />} onClick={() => navigate(`/administration/invitations/invite`)}>
              Invite Users
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate(`/administration/invitations/invite`)}>
            <PersonAddIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderRowActions: ({ row }) => {
      const data: AdministratorInvitation = row.original;
      return (
        <ToolbarButtons>
          <Tooltip title="Cancel Invitation">
            <span>
              <IconButton disabled={data.status !== InvitationStatus.Open} onClick={() => handleCancelInvite(data)}>
                <DeleteIcon color={data.status === InvitationStatus.Open ? "error" : "disabled"} />
              </IconButton>
            </span>
          </Tooltip>
        </ToolbarButtons>
      );
    },
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  }

  return <MRTNoMemo<AdministratorInvitation> options={tableOptions} />;
}
